import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../../components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { PageHeader } from "../../components/admin/PageHeader";
import { MediaCard } from "../../components/admin/MediaCard";
import { Pagination } from "../../components/admin/DataTable";
import { ExportButtons } from "../../components/admin/ExportButtons";
import { useAuth } from "../../context/AuthContext";
import { canDo, fmtDate } from "../../lib/adminApi";
import { supabase } from "../../lib/supabase";

const PAGE_SIZE = 24;
const EXPORT_COLS = [
  { key: "reg", label: "Reg. No", value: (r) => r.profiles?.registration_no },
  { key: "name", label: "Name", value: (r) => r.profiles?.full_name },
  { key: "status", label: "Status" },
  { key: "created_at", label: "Uploaded", value: (r) => fmtDate(r.created_at) },
];

function buildQuery(kind, status, q, count) {
  const table = kind === "photo" ? "profile_photos" : "verification_documents";
  let query = supabase.from(table).select("*, profiles!inner(id, full_name, registration_no)", count ? { count: "exact" } : undefined);
  if (kind === "doc") query = query.eq("type", "id_proof");
  if (status) query = query.eq("status", status);
  const term = q.replace(/[,()*%\\:"]/g, " ").trim();
  if (term) query = query.or(`full_name.ilike.*${term}*,registration_no.ilike.*${term}*`, { referencedTable: "profiles" });
  return query.order("created_at", { ascending: false });
}

export default function PhotoReview() {
  const auth = useAuth();
  const canPhotos = canDo(auth, "can_approve");
  const canDocs = canDo(auth, "can_verify");
  const [kind, setKind] = useState(canPhotos ? "photo" : "doc");
  const [status, setStatus] = useState("pending");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ rows: [], count: 0 });

  const load = useCallback(async () => {
    const from = (page - 1) * PAGE_SIZE;
    const { data: rows, error, count } = await buildQuery(kind, status, q, true).range(from, from + PAGE_SIZE - 1);
    if (error) toast.error(error.message);
    else setData({ rows, count: count || 0 });
  }, [kind, status, q, page]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const canReview = kind === "photo" ? canPhotos : canDocs;
  return (
    <div data-testid="photo-review-page">
      <PageHeader eyebrow="Moderation" title="Photos & ID Review" />
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Tabs value={kind} onValueChange={(v) => { setKind(v); setPage(1); }}>
          <TabsList>
            <TabsTrigger value="photo" data-testid="tab-photos">Photos</TabsTrigger>
            <TabsTrigger value="doc" data-testid="tab-id-proofs">ID proofs</TabsTrigger>
          </TabsList>
        </Tabs>
        <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} data-testid="media-status-filter" className="rmm-select rmm-input h-10 w-40">
          <option value="pending">Pending</option>
          <option value="approved">{kind === "doc" ? "Verified" : "Approved"}</option>
          <option value="rejected">Rejected</option>
          <option value="">All</option>
        </select>
        <div className="relative min-w-[200px] flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8C6A6C]" />
          <Input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Search name or registration no." data-testid="media-search-input" className="rmm-input h-10 bg-white pl-9" />
        </div>
        <ExportButtons fetchRows={async () => (await buildQuery(kind, status, q).limit(5000)).data || []} columns={EXPORT_COLS} name={`${kind}-review`} title="Photos & ID Review" />
      </div>
      {data.rows.length === 0 ? (
        <p data-testid="media-empty" className="rmm-card p-8 text-center text-sm text-[#8C6A6C]">Nothing to review here.</p>
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
          {data.rows.map((item) => (
            <MediaCard key={item.id} item={item} kind={kind} canReview={canReview} onChanged={load}
              caption={<Link to={`/admin/profiles/${item.profiles.id}`} className="block truncate text-xs font-semibold text-[#7A0016] hover:underline">{item.profiles.full_name} · {item.profiles.registration_no}</Link>} />
          ))}
        </div>
      )}
      <Pagination page={page} pageSize={PAGE_SIZE} count={data.count} onPage={setPage} />
    </div>
  );
}
